import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * Replaces the product form's quantity stepper with pack-size radio buttons.
 *
 * Where the theme's Quantity block is present this drives its input, so quantity
 * rules, the sticky add-to-cart label, and every other `quantity-selector:update`
 * listener keep working. Where it has been removed, the selector supplies its own
 * hidden `quantity` field so the form still submits the right number.
 */
class PackSelectorComponent extends Component {
  /** @type {HTMLInputElement | null} */
  #fallbackInput = null;

  #onVariantUpdate = () => {
    // The theme resets quantity to its default when the variant changes, and the
    // subscription app re-renders its own markup. Re-apply once the DOM settles.
    requestAnimationFrame(() => {
      this.#applyQuantity();
      this.#applySubscriptionAvailability();
    });
  };

  connectedCallback() {
    super.connectedCallback();

    const inputs = this.#inputs;
    if (!inputs.length) return;

    if (!inputs.some((input) => input.checked)) inputs[0].checked = true;

    this.#ensureQuantityInput();
    this.#detailsRoot?.classList.add('pack-selector-active');
    this.#applyQuantity();
    this.#applySubscriptionAvailability();

    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#detailsRoot?.classList.remove('pack-selector-active');
    this.#fallbackInput?.remove();
    this.#fallbackInput = null;

    for (const target of this.#subscriptionTargets(this.#sellingPlanInputs)) {
      target.classList.remove('pack-selector-hidden');
    }
  }

  /**
   * Bound declaratively via `on:change` on each radio.
   */
  selectPack() {
    this.#applyQuantity();
    this.#applySubscriptionAvailability();
  }

  /**
   * @returns {HTMLInputElement[]}
   */
  get #inputs() {
    return /** @type {HTMLInputElement[]} */ (Array.from(this.querySelectorAll('.pack-option__input')));
  }

  get #detailsRoot() {
    return this.closest('.product-details');
  }

  /**
   * The form this selector drives. Resolved by id rather than a DOM walk, because
   * product-recommendation cards further down the page have their own buy-buttons.
   *
   * @returns {HTMLFormElement | null}
   */
  get #form() {
    const form = document.getElementById(`BuyButtons-ProductForm-${this.dataset.sectionId}`);

    return form instanceof HTMLFormElement ? form : null;
  }

  get #quantitySelector() {
    return this.#form?.querySelector('quantity-selector-component') ?? null;
  }

  /**
   * The field the form actually submits as `quantity`. Prefers the Quantity
   * block's input; falls back to one this component owns.
   *
   * @returns {HTMLInputElement | null}
   */
  get #quantityInput() {
    // Read through `refs` rather than the `quantityInput` getter, which throws
    // when the input is missing.
    const fromBlock = /** @type {any} */ (this.#quantitySelector)?.refs?.quantityInput;
    if (fromBlock instanceof HTMLInputElement) return fromBlock;

    const own = this.#form?.querySelector('input[name="quantity"]');

    return own instanceof HTMLInputElement ? own : null;
  }

  /**
   * With the Quantity block removed there is no `quantity` field in the form, so
   * Shopify silently adds one of whatever the shopper picked. Supply our own.
   */
  #ensureQuantityInput() {
    if (this.#quantityInput) return;

    const form = this.#form;
    if (!form) return;

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'quantity';
    input.value = '1';
    input.dataset.packSelectorFallback = 'true';
    form.appendChild(input);

    this.#fallbackInput = input;
  }

  #applyQuantity() {
    const input = this.#quantityInput;
    const checked = this.#inputs.find((option) => option.checked);

    if (!input || !checked) return;

    const min = Number(input.min) || 1;
    const max = input.max ? Number(input.max) : Infinity;
    const quantity = Math.min(max, Math.max(min, Number(checked.value) || min));

    if (input.value === String(quantity)) return;

    input.value = String(quantity);

    const selector = /** @type {any} */ (this.#quantitySelector);

    if (typeof selector?.onQuantityChange === 'function') {
      selector.onQuantityChange();
    } else {
      // No Quantity block to broadcast for us, but the sticky add-to-cart bar
      // still needs to know so its "Add to cart (N)" label stays correct.
      this.dispatchEvent(new QuantitySelectorUpdateEvent(quantity));
    }
  }

  /**
   * Some pack sizes are not sold on subscription. While one of those is selected,
   * force one-time purchase and hide the subscription options.
   *
   * The lockout keys off `selling_plan`, the Shopify-standard field name, so it does
   * not depend on the Subscriptions app's private markup. If no such input exists
   * the product has no subscriptions and this is a no-op.
   */
  #applySubscriptionAvailability() {
    const plans = this.#sellingPlanInputs;
    if (!plans.length) return;

    const checked = this.#inputs.find((option) => option.checked);
    const oneTimeOnly = checked?.dataset.disableSubscription === 'true';

    if (oneTimeOnly) {
      // Select the app's own one-time option where it has one, so its UI and the
      // price it writes stay coherent. Not all apps offer one, so this is best-effort only.
      const oneTime = this.#oneTimePurchaseOption(plans);

      if (oneTime && !oneTime.checked) {
        oneTime.checked = true;
        oneTime.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // Hiding is presentation only — a `display: none` control is still submitted.
    // Disabling is what actually keeps `selling_plan` out of the form, and it
    // works regardless of how the app names or values its options.
    for (const plan of plans) {
      if (oneTimeOnly) {
        if (plan.disabled) continue;

        plan.disabled = true;
        plan.dataset.packSelectorDisabled = 'true';
      } else if (plan.dataset.packSelectorDisabled) {
        plan.disabled = false;
        delete plan.dataset.packSelectorDisabled;
      }
    }

    for (const target of this.#subscriptionTargets(plans)) {
      target.classList.toggle('pack-selector-hidden', oneTimeOnly);
    }
  }

  /**
   * The subscription widget's one-time option for the selected variant. Shopify
   * Subscriptions keeps `selling_plan` in a hidden field and names its own radios
   * per variant, so a `selling_plan` control is only the fallback.
   *
   * @param {Array<HTMLInputElement | HTMLSelectElement>} plans
   * @returns {HTMLInputElement | null}
   */
  #oneTimePurchaseOption(plans) {
    const scope = this.closest('.shopify-section') ?? document;
    const variantInput = /** @type {HTMLInputElement | null} */ (this.#form?.querySelector('[name="id"]'));
    const appOption = scope.querySelector(
      `input[type="radio"][data-radio-type="one_time_purchase"][data-variant-id="${variantInput?.value}"]`
    );

    if (appOption instanceof HTMLInputElement) return appOption;

    const fallback = plans.find(
      (plan) => plan instanceof HTMLInputElement && (plan.value === '' || plan.value === '0')
    );

    return fallback instanceof HTMLInputElement ? fallback : null;
  }

  /**
   * Every control the form would submit as `selling_plan` — radios, but also the
   * hidden inputs and `<select>`s some subscription apps use.
   *
   * @returns {Array<HTMLInputElement | HTMLSelectElement>}
   */
  get #sellingPlanInputs() {
    const scope = this.closest('.shopify-section') ?? document;

    return /** @type {Array<HTMLInputElement | HTMLSelectElement>} */ (
      Array.from(scope.querySelectorAll('[name="selling_plan"]'))
    );
  }

  /**
   * What to hide when a one-time-only pack is selected.
   *
   * Prefers a group block wrapping the subscription app block, so a heading the
   * merchant added alongside it ("Choose your plan") hides with it. Never returns
   * a group that also contains this selector — that would hide the whole column.
   *
   * @param {HTMLInputElement[]} plans
   * @returns {Set<Element>}
   */
  #subscriptionTargets(plans) {
    const targets = new Set();

    for (const plan of plans) {
      const block = plan.closest('.shopify-app-block') ?? plan.closest('.shopify-block');
      if (!block) continue;

      const group = block.closest('.group-block');

      targets.add(group && !group.contains(this) ? group : block);
    }

    return targets;
  }
}

if (!customElements.get('pack-selector-component')) {
  customElements.define('pack-selector-component', PackSelectorComponent);
}
